import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { assign } from 'lodash/fp';
import { uniqBy } from 'lodash';
import { RadioConvertUtils } from '../../../../utils/radio/radio';
import { CompanyDetailsFormState } from '../../../buy-process-form/components/company-details/company-details-types';
import { ReservationFormState } from '../../../buy-process-form/components/reservation';
import { RepresentativesFormState } from '../../../buy-process-form/components/representatives/representatives-types';
import { FiscalYearStoreState } from '../../../buy-process-form/components/fiscal-year/fiscal-year-types';
import { AuditorFormState } from '../../../buy-process-form/components/auditor/auditor-types';
import { ProxiesFormState } from '../../../buy-process-form/components/proxies/proxies-types';
import { CostStatementFormState } from '../../../buy-process-form/components/cost-statement/cost-statement-types';
import { RelationType } from '../../../../global/entities/BuyOrder';
import { trimObject } from '../../../../utils/trim-object';
import { Person } from '../../../../global/entities/Person';
import { ClientInfoSaveAction } from '../../../buy-process-form/components/client-info';
import { OwnerSharesFormState } from '../../../buy-process-form/components/owner-shares/owner-shares-types';
import { Utils } from '../../../../utils';
import { BuyOrderApiResponse } from '../buy-order-detailed/buy-order-detailed-types';
import { mapBuyOrderResponse } from '../../../../services/buy-process/get-order/map-from-backend';
import { PersonRelation } from './buy-order-edit-types';
import { pickPersonRelation } from './buy-order-edit-utils';
import { SaveSubmissionInformationAction } from './components/submission-information';
import {
  BUY_ORDER_INITIAL_STATE,
  buyOrderEditStoreInitialState,
} from './buy-order-edit-constants';

const buyOrderEditSlice = createSlice({
  name: 'buyOrderEdit',
  initialState: buyOrderEditStoreInitialState,
  reducers: {
    saveSubmissionInformation(
      state,
      action: PayloadAction<SaveSubmissionInformationAction>,
    ) {
      const { id, internalManager, ...submissionInformation } = action.payload;

      if (internalManager && !state[id].metadata.internalManager) {
        state[id].metadata.internalManager = {
          firstName: internalManager.firstName,
          lastName: internalManager.lastName,
          userId: internalManager.id,
        };
      }

      if (internalManager) {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        state[id].metadata.internalManager!.userId = internalManager.id;
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        state[id].metadata.internalManager!.firstName = internalManager.firstName;
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        state[id].metadata.internalManager!.lastName = internalManager.lastName;
      }

      state[id].metadata.organizationNumber = submissionInformation.organizationNumber;
      state[id].metadata.organizationName = submissionInformation.organizationName;
      state[id].metadata.invoiceNumber = submissionInformation.invoiceNumber;
      state[id].metadata.internalDealDate = submissionInformation.internalDealDate;
      state[id].metadata.registrationDate = submissionInformation.registrationDate;
      state[id].metadata.internalManagerId = submissionInformation.internalManagerId;
    },

    saveEditClientInfo(state, action: PayloadAction<ClientInfoSaveAction>) {
      const { id, ...clientInfo } = action.payload;

      state[id].clientInfo.email = clientInfo.email.trim();
      state[id].clientInfo.emailRepeat = clientInfo.email.trim();
      state[id].clientInfo.phoneNumber = clientInfo.phoneNumber.trim();
      state[id].clientInfo.organizationName = clientInfo.organizationName?.trim() ?? null;
      state[id].clientInfo.relationType = RelationType.ContactPerson;

      if (!clientInfo.socialSecurityNumber) {
        state[id].clientInfo.firstName = clientInfo.firstName;
        state[id].clientInfo.lastName = clientInfo.lastName;
        state[id].clientInfo.address = clientInfo.address;
        state[id].clientInfo.city = clientInfo.city;
        state[id].clientInfo.postalCode = clientInfo.postalCode;

        delete state[id].clientInfo.person;
      } else {
        state[id].clientInfo.person = clientInfo.socialSecurityNumber;

        delete state[id].clientInfo.firstName;
        delete state[id].clientInfo.lastName;
        delete state[id].clientInfo.address;
        delete state[id].clientInfo.city;
        delete state[id].clientInfo.postalCode;
      }
    },

    saveEditCompanyDetails(
      state,
      action: PayloadAction<CompanyDetailsFormState & { id: string }>,
    ) {
      const { id } = action.payload;

      state[id].companyDetails.desiredCompanyName = action.payload.desiredCompanyName;
      state[id].companyDetails.firstAlternativeCompanyName = action.payload.firstAlternativeCompanyName;
      state[id].companyDetails.secondAlternativeCompanyName = action.payload.secondAlternativeCompanyName;
      state[id].companyDetails.nameReview = action.payload.nameReview;
      state[id].companyDetails.co = action.payload.co;
      state[id].companyDetails.address = action.payload.address;
      state[id].companyDetails.postalCode = action.payload.postalCode;
      state[id].companyDetails.city = action.payload.city;
      state[id].companyDetails.municipality = action.payload.municipality;
      state[id].companyDetails.templateSelect = action.payload.templateSelect;
      state[id].companyDetails.businessDescription = action.payload.businessDescription;
      state[id].companyDetails.taxHelp = action.payload.taxHelp;
    },

    saveEditOwnerShares(
      state,
      action: PayloadAction<OwnerSharesFormState & { id: string }>,
    ) {
      const {
        beneficialOwnerHelp,
        numberShares,
        physicalPeople,
        legalShareholders,
        id,
        shareCapital,
      } = action.payload;

      state[id].ownerShares.beneficialOwnerHelp = RadioConvertUtils.strToBool(beneficialOwnerHelp);

      state[id].ownerShares.shareCapital = Number(shareCapital) as
        | 25_000
        | 50_000;

      state[id].ownerShares.numberShares = Number(numberShares) as 250 | 500;

      state[id].ownerShares.physicalPeople = physicalPeople?.map((person) => ({
        numberShares: person.numberShares ? Number(person.numberShares) : 0,
        relationType: RelationType.PhysicalShareHolder,
        person: person.socialSecurityNumber,
        ...(person.relationId && { relationId: person.relationId }),
      })) ?? [];

      state[id].ownerShares.legalShareholders = legalShareholders?.map((legalShareholder) => ({
        numberShares: Number(legalShareholder.numberShares),
        organization: legalShareholder.organizationNumber,
        relationType: RelationType.LegalShareHolder,
        person: legalShareholder.socialSecurityNumber,
      })) ?? [];
    },

    saveEditReservation(
      state,
      action: PayloadAction<ReservationFormState & { id: string }>,
    ) {
      const { id, rightFirstRefusal, stockRestrictions } = action.payload;

      state[id].reservation = {
        stockRestrictions: Number(stockRestrictions),
        rightFirstRefusal: RadioConvertUtils.strToBool(rightFirstRefusal),
      };
    },

    saveEditDocuments(
      state,
      action: PayloadAction<{ id: string; fileName: string }>,
    ) {
      const { id, fileName } = action.payload;
      state[id].metadata.files?.push({ name: fileName });
    },

    saveEditRepresentatives(
      state,
      action: PayloadAction<RepresentativesFormState & { id: string }>,
    ) {
      const {
        id,
        officialCeo,
        ceo,
        representatives,
        signAuthorityUserDefined,
        signAuthority,
      } = action.payload;

      const officialCeoBool = RadioConvertUtils.strToBool(officialCeo);

      const mapRepresentative = (representative: Person) =>
        pickPersonRelation(
          representative,
          representative.relationType || RelationType.None,
        ) as PersonRelation;

      state[id].representatives = assign(state[id].representatives, {
        representatives: representatives.map(mapRepresentative),
        officialCeo: officialCeoBool,
        signAuthorityUserDefined,
        signAuthority,
      });

      state[id].representatives.ceo = ceo && {
        relationType: RelationType.Ceo,
        person: ceo.socialSecurityNumber,
        relationId: state[id].representatives.ceo?.relationId,
      };

      if (!officialCeoBool) {
        if (state[id].representatives.ceo?.relationId) {
          state[id].deleteRequest.push({
            relationId: state[id].representatives.ceo?.relationId,
          });
        }

        delete state[id].representatives.ceo;
      }
    },

    saveEditFiscalYear(
      state,
      action: PayloadAction<FiscalYearStoreState & { id: string }>,
    ) {
      const { id, ...fiscalYear } = action.payload;
      state[id].fiscalYear = assign(state[id].fiscalYear, fiscalYear);
    },

    saveEditAuditor(
      state,
      action: PayloadAction<AuditorFormState & { id: string }>,
    ) {
      const { id, auditorPerson, auditorFirm, auditorOption } = action.payload;

      const auditor = {
        auditorOption,
        auditorFirm: trimObject(auditorFirm as any),
        auditorPerson: trimObject(
          pickPersonRelation(auditorPerson as any, RelationType.None),
        ),
      };

      if (auditorOption === 'no') {
        delete auditor.auditorFirm;
        delete auditor.auditorPerson;
      } else if (auditorOption === 'person') {
        delete auditor.auditorFirm;
      }

      state[id].auditor = {
        ...state[id].auditor,
        ...auditor,
        id: state[id].auditor.id,
      };
    },

    saveEditProxies(
      state,
      action: PayloadAction<ProxiesFormState & { id: string }>,
    ) {
      const { id, bankAccountHelp, ...proxies } = action.payload;

      state[id].proxies = assign(
        state[id].proxies,
        {
          ...pickPersonRelation(proxies as Person, RelationType.PowerOfAttorney),
          bankAccountHelp,
        },
      );
    },

    saveEditCostStatement(
      state,
      action: PayloadAction<CostStatementFormState & { id: string }>,
    ) {
      const { id, receiveOption, receiverEmails, invoiceRecipient, manualHandling, taxAccountOcr } = action.payload;

      state[id].costStatement = assign(state[id].costStatement, {
        receiveOption,
        receiverEmails:
          receiveOption === 'email'
            ? textAreaStringToArray(receiverEmails)
            : state[id].costStatement.receiverEmails,
        digitalCustomerSignature: receiveOption === 'email',
        invoiceRecipient,
        manualHandling,
        taxAccountOcr,
      });
    },

    deleteBuyOrderRelation(
      state,
      action: PayloadAction<{ id: string; relationId: string }>,
    ) {
      const { id, relationId } = action.payload;
      if (!relationId) return;

      state[id].deleteRequest?.push({ relationId });
    },

    deleteBuyOrderOrganization(
      state,
      action: PayloadAction<{ id: string; organizationId: string | string[] }>,
    ) {
      const { id, organizationId } = action.payload;
      if (!organizationId) return;

      const organizationIds = Utils.toArray(organizationId).map((id) => ({
        organizationId: id,
      }));

      state[id].deleteRequest?.push(...organizationIds);

      state[id].deleteRequest = uniqBy(
        state[id].deleteRequest,
        (val) => val.organizationId ?? val.relationId,
      );
    },

    deleteBuyOrderStore(state, action: PayloadAction<{ id: string }>) {
      delete state[action.payload.id];
    },

    resetBuyOrderStore(state, action: PayloadAction<{ id: string }>) {
      state[action.payload.id] = {
        ...BUY_ORDER_INITIAL_STATE,
      };
    },

    saveBuyOrder(
      state,
      action: PayloadAction<{
        buyOrder: BuyOrderApiResponse;
        id: string;
      }>,
    ) {
      state[action.payload.id] = {
        ...mapBuyOrderResponse(action.payload.buyOrder),
        deleteRequest: [],
      };
    },
  },
});

function textAreaStringToArray(str: string) {
  return str.split(String.fromCharCode(10));
}

export const {
  saveEditClientInfo,
  saveEditOwnerShares,
  saveEditCompanyDetails,
  saveEditReservation,
  saveEditRepresentatives,
  saveEditFiscalYear,
  saveEditAuditor,
  saveEditProxies,
  saveEditCostStatement,
  saveBuyOrder,
  saveSubmissionInformation,
  deleteBuyOrderStore,
  deleteBuyOrderOrganization,
  deleteBuyOrderRelation,
  resetBuyOrderStore,
  saveEditDocuments,
} = buyOrderEditSlice.actions;

const buyOrderEditReducer = buyOrderEditSlice.reducer;
export default buyOrderEditReducer;
